import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast from "react-hot-toast";

const NAVY = "#1a237e";

const pageStyles = `
.users-page .status-toggle {
    position: relative;
    display: inline-block;
    width: 34px;
    height: 18px;
    vertical-align: middle;
    margin: 0;
}
.users-page .status-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
}
.users-page .toggle-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: #ef5350;
    transition: 0.2s;
    border-radius: 18px;
}
.users-page .toggle-slider:before {
    position: absolute;
    content: "";
    height: 12px;
    width: 12px;
    left: 3px;
    bottom: 3px;
    background-color: #fff;
    transition: 0.2s;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0,0,0,0.15);
}
.users-page .status-toggle input:checked + .toggle-slider {
    background-color: #43a047;
}
.users-page .status-toggle input:checked + .toggle-slider:before {
    transform: translateX(16px);
}
.users-page .status-toggle input:disabled + .toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
}
.users-page .users-table {
    font-size: 0.82rem;
}
.users-page .users-table thead th {
    background: ${NAVY};
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 0.55rem 0.65rem;
    border: none;
}
.users-page .users-table tbody td {
    padding: 0.5rem 0.65rem;
    vertical-align: middle;
    border-color: #eef0f4;
    color: #374151;
}
.users-page .users-table tbody tr:hover {
    background: #f8f9fc;
}
.users-page .role-chip {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    background: #e8eaf6;
    color: ${NAVY};
    white-space: nowrap;
}
.users-page .status-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
}
.users-page .status-text {
    font-size: 0.7rem;
    font-weight: 600;
    min-width: 42px;
}
.users-page .stat-pill {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 8px;
    padding: 0.55rem 0.75rem;
}
.users-page .stat-pill .label {
    font-size: 0.68rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.users-page .stat-pill .value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
}
`;

export default function AllUsersList() {
    const { http, user } = AuthUser();
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterDept, setFilterDept] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editDepartmentId, setEditDepartmentId] = useState("");

    useEffect(() => {
        fetchUsers();
        fetchDepartments();
    }, []);

    const fetchUsers = async (dept = "", status = "") => {
        setIsLoading(true);
        try {
            const response = await http.get(`/users?login_user=${user.id}&dept=${dept}&status=${status}`);
            setUsers(response.data.data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Failed to fetch users");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await http.get(`/departments?login_user=${user.id}`);
            setDepartments(response.data.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    };

    const handleFilterChange = () => {
        fetchUsers(filterDept, filterStatus);
    };

    const handleClearFilters = () => {
        setFilterDept("");
        setFilterStatus("");
        setSearchTerm("");
        fetchUsers();
    };

    const filteredUsers = users.filter((u) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            u.name?.toLowerCase().includes(searchLower) ||
            u.email?.toLowerCase().includes(searchLower) ||
            u.phone_number?.toLowerCase().includes(searchLower) ||
            u.department?.name?.toLowerCase().includes(searchLower)
        );
    });

    const handleStatusToggle = async (userId, currentStatus) => {
        setUpdatingUserId(userId);
        try {
            const endpoint = currentStatus === 1 ? "/disapprove" : "/approve";
            const response = await http.post(endpoint, { userId });

            if (response.data) {
                setUsers((prevUsers) =>
                    prevUsers.map((u) =>
                        u.id === userId ? { ...u, status: currentStatus === 1 ? 0 : 1 } : u
                    )
                );
                toast.success(`User ${currentStatus === 1 ? "deactivated" : "activated"} successfully!`);
            }
        } catch (error) {
            console.error("Error updating user status:", error);
            toast.error("Failed to update user status");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const openEditModal = (rowUser) => {
        setEditingUser(rowUser);
        setEditDepartmentId(rowUser.department_id || "");
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
        setEditDepartmentId("");
    };

    const handleUpdateDepartment = async (e) => {
        e.preventDefault();

        if (!editDepartmentId) {
            toast.error("Please select a department");
            return;
        }

        try {
            setUpdatingUserId(editingUser.id);
            const response = await http.put(`/users/${editingUser.id}`, {
                name: editingUser.name,
                last_name: editingUser.last_name,
                phone_number: editingUser.phone_number,
                department_id: editDepartmentId,
                designation_name: editingUser.designation_name,
                branch_admin: editingUser.branch_admin,
                teach_class: editingUser.teach_class,
            });

            if (response.data && response.data.data) {
                toast.success("Department updated successfully!");
                closeEditModal();
                fetchUsers(filterDept, filterStatus);
            }
        } catch (error) {
            console.error("Error updating department:", error);
            toast.error(error.response?.data?.error || "Failed to update department");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const activeCount = filteredUsers.filter((u) => u.status === 1).length;
    const inactiveCount = filteredUsers.filter((u) => u.status === 0).length;

    return (
        <>
            <style>{pageStyles}</style>
            <div className="container-fluid mt-3 px-3 pb-4 users-page">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                        <h5 className="fw-bold mb-0">
                            <i className="fa fa-users me-2" style={{ color: NAVY }}></i>
                            All Users List
                        </h5>
                        <small className="text-muted">Manage user access and departments</small>
                    </div>
                    <span
                        className="badge rounded-pill px-3 py-2"
                        style={{ background: "#e8eaf6", color: NAVY, fontWeight: 600, fontSize: "0.75rem" }}
                    >
                        {filteredUsers.length} users
                    </span>
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small fw-semibold mb-1">Search</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Name, email, phone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-semibold mb-1">Department / Role</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={filterDept}
                                    onChange={(e) => setFilterDept(e.target.value)}
                                >
                                    <option value="">All Departments</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small fw-semibold mb-1">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                            </div>
                            <div className="col-md-3 d-flex gap-2">
                                <button type="button" className="btn btn-sm btn-primary" onClick={handleFilterChange}>
                                    <i className="fa fa-filter me-1"></i>Apply
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                                    <i className="fa fa-times me-1"></i>Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {!isLoading && filteredUsers.length > 0 && (
                    <div className="row g-2 mb-3">
                        <div className="col-6 col-md-3">
                            <div className="stat-pill">
                                <div className="label">Total</div>
                                <div className="value" style={{ color: NAVY }}>{filteredUsers.length}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="stat-pill">
                                <div className="label">Active</div>
                                <div className="value text-success">{activeCount}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="stat-pill">
                                <div className="label">Inactive</div>
                                <div className="value text-danger">{inactiveCount}</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="stat-pill">
                                <div className="label">Departments</div>
                                <div className="value" style={{ color: "#0288d1" }}>{departments.length}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="card border-0 shadow-sm">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 mb-0 small text-muted">Loading users...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0 align-middle users-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 48 }}>No.</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Department / Role</th>
                                        <th style={{ width: 120 }}>Status</th>
                                        <th style={{ width: 70 }} className="text-center">Edit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((u, index) => (
                                            <tr key={u.id}>
                                                <td className="text-muted">{index + 1}</td>
                                                <td className="fw-semibold">{u.name} {u.last_name || ""}</td>
                                                <td>
                                                    <span className="text-muted me-1"><i className="fa fa-envelope" style={{ fontSize: "0.7rem" }}></i></span>
                                                    {u.email || "—"}
                                                </td>
                                                <td>
                                                    <span className="text-muted me-1"><i className="fa fa-phone" style={{ fontSize: "0.7rem" }}></i></span>
                                                    {u.phone_number || "—"}
                                                </td>
                                                <td>
                                                    {u.department ? (
                                                        <span className="role-chip">{u.department.name}</span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="status-cell">
                                                        <label className="status-toggle mb-0" title={u.status === 1 ? "Deactivate" : "Activate"}>
                                                            <input
                                                                type="checkbox"
                                                                checked={u.status === 1}
                                                                onChange={() => handleStatusToggle(u.id, u.status)}
                                                                disabled={updatingUserId === u.id}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </label>
                                                        <span className={`status-text ${u.status === 1 ? "text-success" : "text-danger"}`}>
                                                            {updatingUserId === u.id ? "…" : u.status === 1 ? "Active" : "Off"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary py-0 px-2"
                                                        title="Edit department"
                                                        onClick={() => openEditModal(u)}
                                                        style={{ fontSize: "0.75rem", lineHeight: 1.6 }}
                                                    >
                                                        <i className="fa fa-edit"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted py-4">
                                                <i className="fa fa-inbox mb-2 d-block" style={{ fontSize: "1.4rem", opacity: 0.4 }}></i>
                                                No users found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showEditModal && editingUser && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title mb-0">
                                    <i className="fa fa-edit me-2"></i>
                                    Edit Department
                                </h6>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <form onSubmit={handleUpdateDepartment}>
                                <div className="modal-body py-3">
                                    <div className="mb-2">
                                        <div className="small text-muted">User</div>
                                        <div className="fw-semibold">
                                            {editingUser.name} {editingUser.last_name || ""}
                                        </div>
                                        <div className="small text-muted">{editingUser.email}</div>
                                    </div>
                                    <div className="mb-2">
                                        <label className="form-label small fw-semibold mb-1">
                                            Department / Role <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={editDepartmentId}
                                            onChange={(e) => setEditDepartmentId(e.target.value)}
                                            required
                                        >
                                            <option value="">Select Department</option>
                                            {departments.map((dept) => (
                                                <option key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="alert alert-light border small mb-0 py-2">
                                        Changing role updates this user’s access permissions.
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={closeEditModal}
                                        disabled={updatingUserId === editingUser.id}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={updatingUserId === editingUser.id}
                                    >
                                        {updatingUserId === editingUser.id ? "Updating..." : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
